import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { CategoryScale, Chart as ChartJS } from 'chart.js/auto';
import { Map, Marker, Overlay } from 'pigeon-maps';
import { db, auth } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import '../style/Dashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShirt, faBoxOpen, faClipboardCheck, faTshirt, faTruck, faCheckCircle, faBox, faClipboardList  } from '@fortawesome/free-solid-svg-icons';


const Dashboard = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const shop = params.get('shop');

  const [orders, setOrders] = useState([]);
  const [customerLocations, setCustomerLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [BrandInfo, setBrandInfo] = useState('');

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not logged in");
  }

  useEffect(() => {
    const fetchOrders = async () => {
      try {

        const ordersRef = collection(db, 'users', user.uid, 'orders');
        const ordersSnapshot = await getDocs(ordersRef);
        const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setOrders(ordersData);

        // Collect customer locations while checking for valid shipping_address
        const locations = ordersData
          .map(order => {
            const billingCity = order.billing_city;

            if (billingCity && billingCity.latitude && billingCity.longitude) {
              return {
                lat: billingCity.latitude,
                lng: billingCity.longitude,
                address: billingCity,
              };
            }
            return null;
          })
          .filter(location => location !== null);

        setCustomerLocations(locations);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('An error occurred while fetching orders.');
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  useEffect(() => {
    const fetchBrandInfo = async () => {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setBrandInfo({
          brandName: userData.brandName || 'Default Brand Name',
        });
      } else {
        console.log('No such document!');
      }
    };

    fetchBrandInfo();
  }, [user]);


  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  // Calculate product data from all orders (not just confirmed)
  const productCounts = {};
  const productRevenue = {};

  orders.forEach(order => {
    if (Array.isArray(order.order_items)) {
      order.order_items.forEach(item => {
        const productName = item.name;
        const sellingPrice = item.selling_price || 0;
        const totalCost = item.total_cost || 0;
        const netRevenue = sellingPrice - totalCost;

        productCounts[productName] = (productCounts[productName] || 0) + 1;
        productRevenue[productName] = (productRevenue[productName] || 0) + netRevenue;
      });
    }
  });

  // Calculate total orders by date from all orders
  const totalOrdersByDate = {};

  orders.forEach(order => {
    if (Array.isArray(order.order_items)) {
      order.order_items.forEach(item => {
        const sellingPrice = parseFloat(item.selling_price) || 0;

        // Handle Firestore timestamp or regular date
        let orderDate = order.createdAt;
        if (orderDate && orderDate.toDate) {
          orderDate = orderDate.toDate();
        } else {
          orderDate = new Date(orderDate);
        }

        const formattedDate = orderDate.toLocaleDateString();
        totalOrdersByDate[formattedDate] = (totalOrdersByDate[formattedDate] || 0) + sellingPrice;
      });
    }
  });

  // Prepare chart data
  const productTypes = Object.keys(productCounts);
  const pieChartData = {
    labels: productTypes,
    datasets: [
      {
        label: 'Product Breakdown',
        data: productTypes.map(type => productCounts[type]),
        backgroundColor: ['#00589c', '#3ac0da', '#1891c3', '#004d73', '#3dc6c3', '#50e3c2', '#0097A7','#006e8e',
  '#0089a3', '#56c9d9', '#006b7d', '#4bc4d3', '#2a7e8c', '#0096a8'],
      },
    ],
  };

  const barChartData = {
    labels: productTypes,
    datasets: [
      {
        label: 'Product Revenue',
        data: productTypes.map(type => productRevenue[type] || 0),
        backgroundColor: ['#00589c', '#3ac0da', '#1891c3', '#004d73', '#3dc6c3', '#50e3c2', '#0097A7','#006e8e',
          '#0089a3', '#56c9d9', '#006b7d', '#4bc4d3', '#2a7e8c', '#0096a8'],
      },
    ],
  };

  // Prepare data for Line chart (Total Confirmed Orders by Date)
  const totalOrdersDates = Object.keys(totalOrdersByDate).sort((a, b) => new Date(a) - new Date(b));
  const lineChartData = {
    labels: totalOrdersDates,
    datasets: [
      {
        label: 'Total Confirmed Order Revenue by Date',
        data: totalOrdersDates.map(date => totalOrdersByDate[date]),
        fill: false,
        backgroundColor: '#00bfff',
        borderColor: '#00bfff',
        borderWidth: 2,
      },
    ],
  };

return (
  <div className="flex h-screen bg-white font-sans">
    {/* Sidebar fixed on left */}
    <Sidebar/>
    {/* Main content area */}
    <div className="flex flex-col flex-1 md:ml-64 overflow-y-auto">
      <Header />

      <main className="p-6 space-y-8">
        {/* Welcome */}
        <h1 className="text-xl md:3xl font-bold text-gray-800 uppercase dashboard-welcome-title">
          Welcome to <span className="text-blue-600">{BrandInfo.brandName}</span>'s Dashboard
        </h1>

        {/* Cards Section */}

<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
  {/* Create Product */}
  <Link
    to={
      user.uid === "hl6gcaUDIFXQkYUxppfAAHycLtO2" ||
      user.uid === "Ge9xCYPWAqcixxj6wzfpHd1XWPF2"
        ? "/select-tshirt"
        : "/SelectTshirt"
    }
    className="bg-white border border-black rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:shadow-xl hover:scale-[1.02] hover:border-blue-500 hover:bg-gradient-to-br hover:from-white hover:to-blue-50 transition-all duration-300 group no-underline hover:no-underline focus:no-underline active:no-underline dashboard-link"
    style={{ textDecoration: 'none' }}
  >
    {/* Icon circle */}
    <div className="w-12 h-12 min-w-12 min-h-12 bg-black rounded-full flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-blue-800 transition-all duration-300">
      <FontAwesomeIcon
        icon={faBox}
        className="text-white text-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300"
      />
    </div>

    {/* Text */}
    <p className="font-semibold text-sm uppercase tracking-wide text-gray-800 group-hover:text-blue-700 transition-colors duration-300">
      Create Product
    </p>
  </Link>

  {/* Book a Sample */}
  <Link
    to="/create-order"
    className="bg-white border border-black rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:shadow-xl hover:scale-[1.02] hover:border-green-500 hover:bg-gradient-to-br hover:from-white hover:to-green-50 transition-all duration-300 group no-underline hover:no-underline focus:no-underline active:no-underline dashboard-link"
    style={{ textDecoration: 'none' }}
  >
    <div className="w-12 h-12 min-w-12 min-h-12 bg-black rounded-full flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-green-600 group-hover:to-green-800 transition-all duration-300">
      <FontAwesomeIcon
        icon={faTruck}
        className="text-white text-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300"
      />
    </div>
    <p className="font-semibold text-sm uppercase tracking-wide text-gray-800 group-hover:text-green-700 transition-colors duration-300">
      Book a Sample
    </p>
  </Link>

  {/* Order Management */}
  <Link to="/order-management" className="block no-underline hover:no-underline focus:no-underline active:no-underline dashboard-link" style={{ textDecoration: 'none' }}>
    <div className="bg-white border border-black rounded-2xl shadow-sm p-4 flex items-center gap-4 hover:shadow-xl hover:scale-[1.02] hover:border-purple-500 hover:bg-gradient-to-br hover:from-white hover:to-purple-50 transition-all duration-300 group cursor-pointer">
      <div className="w-12 h-12 min-w-12 min-h-12 bg-black rounded-full flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-purple-800 transition-all duration-300">
        <FontAwesomeIcon
          icon={faClipboardList}
          className="text-white text-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300"
        />
      </div>
      <span className="font-semibold text-sm uppercase tracking-wide text-gray-800 group-hover:text-purple-700 transition-colors duration-300">
        Order Management
      </span>
    </div>
  </Link>
</div>



        {/* Line Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-md">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Total Confirmed Orders by Date
          </h3>
          <Line data={lineChartData} />
        </div>

        {/* Charts & Map */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Product Breakdown</h3>
            <Pie data={pieChartData} />
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Product Revenue Breakdown</h3>
            <Bar data={barChartData} />
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-md relative">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Places You Reached</h3>
            <div className="relative">
              <Map height={300} defaultCenter={[20.5937, 78.9629]} defaultZoom={4}>
                {customerLocations.map((location, index) => (
                  <Marker
                    key={index}
                    width={50}
                    anchor={[location.lat, location.lng]}
                    onClick={() => setSelectedLocation(location)}
                  />
                ))}
                {selectedLocation && (
                  <Overlay anchor={[selectedLocation.lat, selectedLocation.lng]} offset={[0, 0]}>
                    <div className="bg-white p-2 rounded shadow">{selectedLocation.address}</div>
                  </Overlay>
                )}
              </Map>

              {/* Coming Soon Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl">
                <h2 className="text-white text-xl font-bold">Coming Soon</h2>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

};

export default Dashboard;
